import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface InboxMessage {
  id: string;
  title: string;
  body: string;
  type: "personal" | "naujiena" | "nuolaida";
  published_at: string | null;
  created_at: string;
  read: boolean;
}

export const INBOX_TYPE_LABEL: Record<string, string> = {
  personal: "Asmeninis",
  naujiena: "Naujiena",
  nuolaida: "Nuolaida",
};

export function useInbox() {
  const { user, loading: authLoading } = useAuth();
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setMessages([]);
      setLoading(false);
      return;
    }
    const [{ data: rows }, { data: reads }] = await Promise.all([
      supabase
        .from("inbox_messages")
        .select("id, title, body, type, published_at, created_at")
        .order("published_at", { ascending: false }),
      supabase.from("inbox_reads").select("message_id").eq("user_id", user.id),
    ]);
    const readIds = new Set((reads ?? []).map((r) => r.message_id as string));
    setMessages(
      ((rows ?? []) as Omit<InboxMessage, "read">[]).map((m) => ({
        ...m,
        read: readIds.has(m.id),
      })),
    );
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    void load();
  }, [authLoading, load]);

  useEffect(() => {
    const refresh = () => void load();
    window.addEventListener("inbox-updated", refresh);
    return () => window.removeEventListener("inbox-updated", refresh);
  }, [load]);

  const markRead = useCallback(
    async (messageId: string) => {
      if (!user) return;
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, read: true } : m)));
      await supabase
        .from("inbox_reads")
        .upsert({ message_id: messageId, user_id: user.id }, { onConflict: "message_id,user_id" });
      window.dispatchEvent(new Event("inbox-updated"));
    },
    [user],
  );

  const unread = messages.filter((m) => !m.read).length;

  return { messages, unread, loading: loading || authLoading, refetch: load, markRead };
}
